import React, { useEffect } from "react";
import Modal from "./Modal";
import { useTranslation } from "react-i18next";
import { calculateDurationInHoursAndMin, stripHtml } from "../../utils/serviceUtilities";
import ImageWithFallback from "../ImageWithFallback";
import VideoPlayer from "../VideoPlayer/VideoPlayer";

const MODAL_ID = "course-details-modal";

export default function CourseDetailsModal({ course, isOpened, onClose }) {
  const { t } = useTranslation("learningPaths");
  const { t: commonT } = useTranslation("common");

  useEffect(() => {
    const el = document.getElementById(MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (isOpened && course) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [isOpened, course]);

  useEffect(() => {
    const el = document.getElementById(MODAL_ID);
    if (!el) return;
    const handleHidden = () => onClose?.();
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, [onClose]);

  if (!course) return null;
  const tutorName = course.tutor?.name ?? "-";
  const tutorTitle = course.tutor?.title ?? "";
  const tutorAvatar = course.tutor?.avatar_url ?? "";
  const keyPoints = course.key_points ?? [];
  const details = course.details ?? "";
  const level = course.level ?? "";
  const videoDuration = course.video_duration ?? 0;

  const formatDuration = (seconds) => {
    let result = "";
   const {hours, minutes} = calculateDurationInHoursAndMin(seconds);
    if (hours > 0) result += `${hours}${t("learningPaths:hour")} : `;
    result += `${minutes} ${t("learningPaths:minutes")}`;
    return result;
  };

  return (
    <Modal
      id={MODAL_ID}
      title=""
      hasCloseBtn
      isOpened={isOpened}
      onClose={onClose}
      maxWidth={640}
      isVerticallyCentered
      className="course-details-modal"
    >
      <div className="course-details-modal__content">
        <div className="mb-4">
          {course.promo_video_url ? (
            <div style={{ borderRadius: "8px", overflow: "hidden" }}>
              <VideoPlayer url={course.promo_video_url} autoPlay={false} />
            </div>
          ) : (
            course.image_url && (
              <img
                src={course.image_url}
                alt=""
                className="course-details-modal__image mb-3"
                style={{
                  width: "100%",
                  maxHeight: "240px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            )
          )}
        </div>

        <div className="d-flex align-items-center gap-2 mb-2">
            {course?.topics?.[0]?.title && (
                <span className="badge font_12 d-inline-block text-white" style={{ backgroundColor: course.topics[0].color || 'rgb(87, 158, 137)' }}>
                {course.topics[0].title}
                </span>
            )}
            {level && (
                <span className="badge bg-secondary font_12">{commonT(`levels.${level}`) || level}</span>
            )}
        </div>
        <h2 className="font_18 font-extrabold mb-2">{course.title}</h2>
        <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded-3">
          <div className="d-flex align-items-center gap-3">
            {tutorAvatar ? (
              <ImageWithFallback
                src={tutorAvatar}
                alt={tutorName}
                fallback={'/images/user-empty.png'}
                width="50"
                height="50"
                className="rounded-circle object-fit-cover"
              />
            ) : (
                <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white" style={{width: 50, height: 50}}>
                    {tutorName.charAt(0)}
                </div>
            )}
            <div>
              <div className="font-extrabold font_16">{tutorName}</div>
              {tutorTitle && <div className="font_14 text-muted">{tutorTitle}</div>}
            </div>
          </div>
          
          <div className="text-end">
             {course.sections_count > 0 && (
                <div className="mb-1 font_14 font-extrabold">
                  <i className="fas fa-layer-group me-2"></i>
                  {course.sections_count} {t("learningPaths:sections")}
                </div>
             )}
             {course.lessons_count > 0 && (
                <div className="mb-1 font_14 font-extrabold">
                  <i className="fas fa-play-circle me-2"></i>
                  {course.lessons_count} {t("learningPaths:lesson")}
                </div>
             )}

             {videoDuration > 0 && (
                <div className="mb-1 font_14 font-extrabold">
                  <i className="fas fa-clock fa-fw me-2"></i>
                  {formatDuration(videoDuration)}
                </div>
             )}
          </div>
        </div>

        {details && (
          <div className="mb-3">
            <h3 className="font_14 font-extrabold mb-2">
              {t("learningPaths:courseDetails")}
            </h3>
            <p className="font_14 mb-0">{details}</p>
          </div>
        )}

        {course.sections && course.sections.length > 0 && (
          <div className="mb-4 course-content">
            <h3 className="font_14 font-extrabold mb-2">
              {t("learningPaths:courseContent")}
            </h3>
            <div className="accordion" id="courseContentAccordion">
              {course.sections.map((section, index) => {
                 const sectionDuration = section.lessons?.reduce((acc, lesson) => acc + (lesson.video_duration || 0), 0) || 0;
                 const sectionDurationStr = formatDuration(sectionDuration);
                 return (
                <div className="accordion-item border-0 mb-2" key={section.id}>
                  <h2 className="accordion-header" id={`heading${section.id}`}>
                    <button
                      className="accordion-button collapsed p-3 bg-light rounded-3 font_14 font-bold shadow-none d-flex align-items-center w-100"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse${section.id}`}
                      aria-expanded="false"
                      aria-controls={`collapse${section.id}`}
                      style={{textAlign: 'inherit'}}
                    >
                        <i className="fas fa-chevron-down me-3 text-muted"></i>
                        <span className="text-dark flex-grow-1">{section.name}</span>
                        <span className="font_12 text-muted font-normal ms-2 text-nowrap">
                             {section.lessons?.length || 0} {t("learningPaths:lectures")} • {sectionDurationStr}
                        </span>
                    </button>
                  </h2>
                  <div
                    id={`collapse${section.id}`}
                    className="accordion-collapse collapse"
                    aria-labelledby={`heading${section.id}`}
                    data-bs-parent="#courseContentAccordion"
                  >
                    <div className="accordion-body p-0">
                      <ul className="list-group list-group-flush">
                        {section.lessons &&
                          section.lessons.map((lesson) => (
                            <li
                              key={lesson.id}
                              className="list-group-item border-0 px-3 py-2 font_13 text-muted d-flex align-items-center justify-content-between"
                            >
                              <div className="d-flex align-items-center">
                                  <i className={`fas ${lesson.type === 'ExplanationFile' ? 'fa-file-lines' : 'fa-play-circle'} me-2 font_12 text-primary opacity-50`}></i>
                                  <span>{lesson.name}</span>
                              </div>
                              <span className="font_12 text-muted op-7">
                                {formatDuration(lesson.video_duration)}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        )}
        {keyPoints.length > 0 && (
          <div className="mb-4">
            <h3 className="font_14 font-extrabold mb-2">
              {t("learningPaths:keyPoints")}
            </h3>
            <ul className="ps-3 mb-0 list-unstyled">
              {keyPoints.map((point, index) => (
                <li
                  key={index}
                  className="font_14 mb-2 d-flex align-items-start"
                  style={{ listStyle: "none", paddingInlineStart: "6px" }}
                >
                  <span
                    className="flex-shrink-0 mt-2 me-2"
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#6c757d",
                    }}
                    aria-hidden
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {course.tutor?.bio && (
            <div className="mb-4">
                <h3 className="font_14 font-extrabold mb-2">
                    {t("learningPaths:aboutTutor")}
                </h3>
                <div className="font_14 text-muted bg-light p-3 rounded-3" style={{whiteSpace: 'pre-line'}}>
                    {stripHtml(course.tutor.bio)}
                </div>
            </div>
        )}
      </div>
    </Modal>
  );
}
